"use client";

import { StepTabs } from "@/components/ui/step-tabs";
import { useFixedTabTimer } from "@/hooks/use-fixed-tab-timer";
import { cn } from "@/lib/utils";

import { CATEGORIES } from "./data";
import { TOOL_PREVIEW_COMPONENTS } from "./previews";
import { STAGE_TAB_ICONS } from "./stage-tab-icons";
import { ToolCard } from "./tool-card";

/**
 * Tabbed toolkit module (Figma 11043:69724) — four stage tabs with a fixed
 * 10-second auto-advance timer and looping progress indicator.
 */
export function ToolkitTabs() {
  const { active, progress, selectTab } = useFixedTabTimer(CATEGORIES.length);
  const category = CATEGORIES[active] ?? CATEGORIES[0];

  if (!category) return null;

  const tabs = CATEGORIES.map((cat) => ({
    id: cat.id,
    label: cat.label,
    icon: STAGE_TAB_ICONS[cat.id as keyof typeof STAGE_TAB_ICONS],
  }));

  return (
    <section
      id="toolkit"
      data-testid="tools-toolkit-tabs"
      className="border-grey-100 bg-grey-50 scroll-mt-24 border-t px-2.5 pb-20 lg:px-2.5"
    >
      <div className="mx-auto w-full max-w-[1280px]">
        <StepTabs
          tabs={tabs}
          active={active}
          progress={progress}
          onSelect={selectTab}
          ariaLabel="Toolkit stages"
          className="border-t-0 border-b-0"
          topCornerConnectors="tee-both"
          collapseInactiveOnMobile
        />

        <div className="border-grey-100 relative border bg-white p-6 md:p-16">
          <div className="flex flex-col gap-8">
            <h2 className="text-ds-heading-h3 text-grey-950 tracking-[-0.04em]">
              {category.label}
            </h2>

            <div
              role="tabpanel"
              aria-label={category.label}
              className={cn(
                "grid grid-cols-1 gap-5",
                category.tools.length >= 3 && "md:grid-cols-2 lg:grid-cols-3",
                category.tools.length === 2 && "md:grid-cols-2"
              )}
            >
              {category.tools.map((tool) => {
                const previewConfig = TOOL_PREVIEW_COMPONENTS[tool.title];
                const Preview = previewConfig?.Preview;
                return (
                  <ToolCard
                    key={tool.title}
                    tool={tool}
                    previewVariant={previewConfig?.variant}
                    preview={Preview ? <Preview /> : undefined}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
